import React, { useState, useEffect } from 'react';
import { useGetWhatsAppMediaUrlQuery, useGetMediaProxyUrlQuery } from '../../store/services/whatsappApi';

type ButtonType = {
  text: string;
  url?: string;
  phone_number?: string;
  type: 'URL' | 'PHONE_NUMBER' | 'QUICK_REPLY';
};

type TemplateComponentType = {
  type: string;
  format?: string;
  text?: string;
  buttons?: ButtonType[];
};

type TemplateType = {
  id: string | number;
  name: string;
  status: string;
  language: string;
  category: string;
  components: TemplateComponentType[];
  variables?: string[];
  media_handle?: string;
  header_format?: string;
  media_url?: string;
};

interface TemplatePreviewProps {
  template: TemplateType | null;
  variables?: Record<string, string>;
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template, variables = {} }) => {
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [phoneNumberId, _setPhoneNumberId] = useState<string>('783696321494612'); // Default phone number ID
  
  // Check if the media handle is a WhatsApp Business API media ID (numeric)
  const isWhatsAppMediaId = template?.media_handle && /^\d+$/.test(template.media_handle);
  
  // Get the direct proxy URL for the media (no API call, just URL construction)
  const { data: proxyUrl } = useGetMediaProxyUrlQuery(
    { mediaId: template?.media_handle || '', phoneNumberId },
    { skip: !isWhatsAppMediaId }
  );
  
  // We still need to check if the media exists via the WhatsApp API
  // This helps us know if the media is valid before trying to display it
  const { data: mediaUrlData, isLoading } = useGetWhatsAppMediaUrlQuery(
    { mediaId: template?.media_handle || '', phoneNumberId },
    { skip: !isWhatsAppMediaId }
  );
  
  // Update media URL when data is available
  useEffect(() => {
    if (mediaUrlData && mediaUrlData.url && proxyUrl) {
      // Use the proxy URL instead of the direct WhatsApp URL
      setMediaUrl(proxyUrl);
    }
  }, [mediaUrlData, proxyUrl]);
  
  if (!template) {
    return (
      <div className="bg-gray-100 p-6 rounded-lg text-center">
        <p className="text-gray-500">Select a template to preview</p>
      </div>
    );
  }

  // Replace variables in text with their values or placeholder
  const replaceVariables = (text: string) => {
    if (!text) return '';
    
    // Replace all {{variable}} occurrences with actual values or placeholders
    return text.replace(/{{([^}]+)}}/g, (match, variableName) => {
      return variables[variableName] || `[${variableName}]`;
    });
  };

  // Find header, body, footer components
  const headerComponent = template.components?.find(c => c.type === 'HEADER');
  const bodyComponent = template.components?.find(c => c.type === 'BODY');
  const footerComponent = template.components?.find(c => c.type === 'FOOTER');
  const buttonsComponent = template.components?.find(c => c.type === 'BUTTONS');

  // Extract buttons if available
  const buttons = buttonsComponent?.buttons || [];

  return (
    <div className="flex flex-col items-center">
      <div className="bg-white shadow-md rounded-lg w-full max-w-sm border border-gray-200 overflow-hidden">
        {/* Phone header simulation */}
        <div className="bg-whatsapp-green text-white p-3 flex justify-between items-center">
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 2a8 8 0 100 16 8 8 0 000-16zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">WhatsApp Business</span>
          </div>
          <div className="text-xs">Template Preview</div>
        </div>

        {/* Message container */}
        <div className="bg-gray-100 p-4 min-h-[300px]">
          <div className="flex justify-end mb-4">
            <div className="bg-white rounded-lg shadow-sm p-3 max-w-[80%] ml-auto">
              <div className="text-gray-600 text-sm">
                You requested information from our business
              </div>
            </div>
          </div>
          
          <div className="flex mb-4">
            <div className="bg-white rounded-lg shadow-sm p-3 max-w-[80%] relative template-message">
              {/* Business name */}
              <div className="text-whatsapp-teal text-xs font-medium mb-1">
                Your Business
              </div>
              
              {/* Template header */}
              {headerComponent && (
                <div className="mb-2">
                  {headerComponent.format === 'TEXT' && (
                    <div className="font-medium">{replaceVariables(headerComponent.text || '')}</div>
                  )}
                  {headerComponent.format === 'IMAGE' && (
                    <div className="mb-2">
                      {mediaUrl ? (
                        <div className="relative">
                          <div className="bg-gray-200 h-40 w-full rounded-lg flex items-center justify-center mb-1 overflow-hidden">
                            <img 
                              src={mediaUrl} 
                              alt="Template media" 
                              className="object-contain max-h-full max-w-full"
                              onError={(e) => {
                                // Handle image load error
                                const target = e.target as HTMLImageElement;
                                target.onerror = null;
                                target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDE2IDEwIDUgMjEiPjwvcG9seWxpbmU+PC9zdmc+';
                              }}
                            />
                          </div>
                        </div>
                      ) : template.media_handle ? (
                        <div className="relative">
                          <div className="bg-gray-200 h-40 w-full rounded-lg flex items-center justify-center mb-1 overflow-hidden">
                            {isLoading ? (
                              <div className="text-center p-2">
                                <svg className="animate-spin h-10 w-10 text-gray-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <p className="text-xs text-gray-600 mt-1">Loading media...</p>
                              </div>
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                <div className="text-center p-2">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-green-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <p className="text-xs text-gray-600 mt-1">Media uploaded</p>
                                  <p className="text-xs text-gray-500 break-all">{template.media_handle.substring(0, 10)}...</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-200 h-40 w-full rounded-lg flex items-center justify-center mb-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  )}
                  {headerComponent.format === 'VIDEO' && (
                    <div className="mb-2">
                      {template.media_handle ? (
                        <div className="relative">
                          <div className="bg-gray-200 h-40 w-full rounded-lg flex items-center justify-center mb-1 overflow-hidden">
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                              <div className="text-center p-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-green-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="text-xs text-gray-600 mt-1">Video uploaded</p>
                                <p className="text-xs text-gray-500 break-all">{template.media_handle.substring(0, 10)}...</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-200 h-40 w-full rounded-lg flex items-center justify-center mb-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  )}
                  {headerComponent.format === 'DOCUMENT' && (
                    <div className="mb-2">
                      {template.media_handle ? (
                        <div className="bg-gray-200 p-3 rounded-lg flex items-center mb-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <div>
                            <span className="text-gray-700 text-sm font-medium">Document uploaded</span>
                            <p className="text-xs text-gray-500 break-all">{template.media_handle.substring(0, 15)}...</p>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-200 p-3 rounded-lg flex items-center mb-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span className="text-gray-600 text-sm">document.pdf</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Template body */}
              {bodyComponent && (
                <div className="text-gray-800 mb-2 whitespace-pre-wrap">
                  {replaceVariables(bodyComponent.text || '')}
                </div>
              )}
              
              {/* Template footer */}
              {footerComponent && (
                <div className="text-gray-500 text-xs">
                  {replaceVariables(footerComponent.text || '')}
                </div>
              )}
              
              {/* Template buttons */}
              {buttons.length > 0 && (
                <div className="mt-3 pt-2 border-t border-gray-200">
                  {buttons.map((button, index) => (
                    <div 
                      key={index} 
                      className={`text-center py-2 ${index > 0 ? 'border-t border-gray-200' : ''}`}
                    >
                      <button 
                        className="text-whatsapp-teal font-medium text-sm"
                      >
                        {button.text}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Time stamp */}
              <div className="text-gray-500 text-xs text-right mt-1">
                {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Template information */}
      <div className="mt-4 text-center w-full max-w-sm">
        <h4 className="font-medium text-gray-700">{template.name}</h4>
        <p className="text-gray-500 text-sm">
          {template.category} • {template.language}
        </p>
        {template.variables && template.variables.length > 0 && (
          <div className="mt-2 text-sm text-gray-600">
            <p className="font-medium">Variables:</p>
            <div className="flex flex-wrap gap-1 justify-center mt-1">
              {template.variables.map((variable, index) => (
                <span 
                  key={index} 
                  className="bg-gray-100 px-2 py-1 rounded-md text-xs"
                >
                  {variable}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatePreview;
